import { formatAmount } from '../lib/money';
import { useState, useEffect } from 'react';
import { useCheckoutStore } from '../store/checkout';
import { getMarketConfig } from '../config/market';
import { getShippingOptions, selectShipping as selectShippingApi } from '../api/client';
import type { FreeShippingProgress } from '../api/client';
import { Button } from '../components/Button';
import styles from './ShippingStep.module.css';

interface ShippingOption {
  id: string;
  name?: string;
  displayName?: string;
  description?: string;
  price: number;
  estimatedDelivery?: string | { date?: string; display?: string };
  provider: string;
  isPickup?: boolean;
  logoUrl?: string;
  isCheapest?: boolean;
  isFastest?: boolean;
  // Pricing display fields
  originalPrice?: number;
  discountAmount?: number;
  pricingLabel?: string;
  showOriginalPrice?: boolean;
  showSavings?: boolean;
  isFreeShipping?: boolean;
}

// Extended type for API response options that may have extra fields
interface ApiShippingOption {
  id: string;
  name?: string;
  displayName?: string;
  description?: string;
  price: { amount: number; currency?: string };
  estimatedDelivery?: string | { date?: string; display?: string };
  provider: string;
  isPickup?: boolean;
  logoUrl?: string;
  isCheapest?: boolean;
  isFastest?: boolean;
  // Pricing display fields from API
  originalPrice?: { amount: number; currency?: string };
  discountAmount?: number;
  pricingLabel?: string;
  pricingMessage?: string;
  showOriginalPrice?: boolean;
  showSavings?: boolean;
  isFreeShipping?: boolean;
}

// Helper to get display string from estimatedDelivery
const getDeliveryDisplay = (delivery?: string | { date?: string; display?: string }, fallback = 'Ukjent leveringstid'): string => {
  if (!delivery) return fallback;
  if (typeof delivery === 'string') return delivery;
  return delivery.display || delivery.date || fallback;
};

/**
 * Delivery estimate for merchant-supplied shipping options.
 *
 * Custom options are mapped here on the client and never reach the shipping
 * endpoint, so the server's estimate never arrives — a merchant who sends
 * `deliveryDays` and no `estimatedDelivery` got "Ukjent leveringstid" on every
 * option. Mirrors the server: business days, and 0/1 read as today/tomorrow.
 */
const deliveryFromDays = (days?: number): { date: string; display: string } | undefined => {
  if (typeof days !== 'number' || days < 0) return undefined;

  const d = new Date();
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) added++;
  }

  const display =
    days === 0 ? 'I dag'
    : days === 1 ? 'I morgen'
    : d.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' });

  return { date: d.toISOString().split('T')[0], display };
};

// Provider logo URLs
const PROVIDER_LOGOS: Record<string, string> = {
  BRING: 'https://res.cloudinary.com/martinsurf/image/upload/v1770409586/surfboardmerchant/anorxthnulw8cmq45exy.png',
  POSTEN: 'https://res.cloudinary.com/martinsurf/image/upload/v1770409586/surfboardmerchant/anorxthnulw8cmq45exy.png',
  POSTNORD: 'https://res.cloudinary.com/martinsurf/image/upload/v1770409601/surfboardmerchant/vgwzzprxfbtizngtfnzc.png',
  HELTHJEM: 'https://res.cloudinary.com/martinsurf/image/upload/v1770409617/surfboardmerchant/ogilnavdbpdquyvbuxd1.png',
};

const getProviderLogo = (option: ShippingOption) => {
  const provider = option.provider.toUpperCase();
  
  // If option has a custom logoUrl, use that
  if (option.logoUrl) {
    return <img src={option.logoUrl} alt={option.provider} className={styles.logoImg} />;
  }
  
  // Check provider logo map
  if (PROVIDER_LOGOS[provider]) {
    return <img src={PROVIDER_LOGOS[provider]} alt={option.provider} className={styles.logoImg} />;
  }
  
  // Text fallback per provider
  if (provider === 'BRING' || provider === 'POSTEN') {
    return <span className={styles.logoTextPosten}>Posten</span>;
  }
  if (provider === 'POSTNORD') {
    return <span className={styles.logoTextPostnord}>PostNord</span>;
  }
  if (provider === 'HELTHJEM') {
    return <span className={styles.logoTextHelthjem}>Helthjem</span>;
  }
  
  // "CUSTOM" is the internal marker for a merchant-supplied option, not a
  // carrier name — rendering it puts the literal word "custom" above every
  // shipping choice. Show nothing rather than leaking an implementation detail.
  if (provider === 'CUSTOM' || provider === 'OTHER') {
    return null;
  }

  return <span className={styles.logoTextDefault}>{option.provider}</span>;
};

export function ShippingStep() {
  const { 
    setStep, 
    selectShipping, 
    shippingAddress, 
    sessionId,
    customShippingOptions,
    useBuiltInShipping,
    skipShipping,
  } = useCheckoutStore();
  const locale = useCheckoutStore((s) => s.locale);
  const market = getMarketConfig(locale);
  const L = market.labels;
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [savingShipping, setSavingShipping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState<ShippingOption[]>([]);
  const [freeShippingProgress, setFreeShippingProgress] = useState<FreeShippingProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  // If shipping is skipped, go directly to payment
  useEffect(() => {
    if (skipShipping) {
      // Set a dummy free shipping option
      selectShipping({
        id: 'digital',
        provider: 'none',
        method: 'digital',
        displayName: L.digitalDelivery,
        description: L.noPhysicalDelivery,
        price: { amount: 0, currency: market.defaultCurrency },
        deliveryDays: 0,
        isPickup: false,
      });
      setStep('payment');
    }
  }, [skipShipping, selectShipping, setStep]);

  // Fetch shipping options (built-in or custom)
  useEffect(() => {
    if (skipShipping) return;
    
    const fetchShippingOptions = async () => {
      const postalCode = shippingAddress?.postalCode;
      
      // Use custom shipping options if provided
      if (customShippingOptions && customShippingOptions.length > 0) {
        const mappedOptions: ShippingOption[] = customShippingOptions.map((opt) => ({
          id: opt.id,
          name: opt.name,
          description: opt.description || '',
          price: typeof opt.price === 'object' ? opt.price.amount : opt.price,
          estimatedDelivery: opt.estimatedDelivery ?? deliveryFromDays((opt as { deliveryDays?: number }).deliveryDays),
          provider: opt.provider || 'custom',
          isPickup: opt.isPickup || false,
          logoUrl: opt.logoUrl,
        }));
        
        setOptions(mappedOptions);
        // Auto-select cheapest option. Push it to the store too, so the order
        // summary reflects the option that is already highlighted rather than
        // waiting for the customer to touch something.
        const cheapest = mappedOptions.reduce((prev, curr) => 
          curr.price < prev.price ? curr : prev
        );
        setSelectedOption(cheapest.id);
        selectShipping(toStoreShipping(cheapest));
        setIsLoading(false);
        return;
      }
      
      // Use built-in Bring/Posten integration
      if (!useBuiltInShipping) {
        setError(L.noShippingConfigured);
        setIsLoading(false);
        return;
      }
      
      if (!postalCode || !sessionId) {
        setError('Mangler postnummer eller session');
        setIsLoading(false);
        return;
      }

      try {
        const response = await getShippingOptions(sessionId, postalCode);
        
        if (response.error) {
          setError(response.error.message);
          setIsLoading(false);
          return;
        }
        
        if (response.data?.options && response.data.options.length > 0) {
          // Map API response - keep prices in minor units (øre)
          const mappedOptions: ShippingOption[] = response.data.options.map((opt: ApiShippingOption) => ({
            id: opt.id,
            name: opt.name || opt.displayName,
            displayName: opt.displayName || opt.name,
            description: opt.description,
            price: opt.price.amount, // Keep in minor units (øre)
            estimatedDelivery: opt.estimatedDelivery,
            provider: opt.provider,
            isPickup: opt.isPickup || opt.id.toLowerCase().includes('pickup') || opt.id.toLowerCase().includes('servicepakke') || opt.id.toLowerCase().includes('hentested'),
            logoUrl: opt.logoUrl,
            isCheapest: opt.isCheapest,
            isFastest: opt.isFastest,
            // Pricing display fields
            originalPrice: opt.originalPrice?.amount,
            discountAmount: opt.discountAmount,
            pricingLabel: opt.pricingLabel,
            showOriginalPrice: opt.showOriginalPrice,
            showSavings: opt.showSavings,
            isFreeShipping: opt.isFreeShipping,
          }));
          
          setOptions(mappedOptions);
          
          // Store free shipping progress if available
          if ((response.data as any).freeShippingProgress) {
            setFreeShippingProgress((response.data as any).freeShippingProgress);
          }
          
          // Auto-select cheapest option, and tell the store so the summary
          // matches the highlighted choice from the start.
          const cheapest = mappedOptions.reduce((prev, curr) => 
            curr.price < prev.price ? curr : prev
          );
          setSelectedOption(cheapest.id);
          selectShipping(toStoreShipping(cheapest));
        } else {
          // The endpoint answers 200 with the reason inside the payload, so a
          // configuration problem never reaches `response.error`. Without this
          // the customer was told "Ingen fraktalternativer tilgjengelig for
          // dette postnummeret" when the real answer was
          // NO_SHIPPING_CONFIGURED — nothing to do with their postal code, and
          // no hint to the merchant about what is actually missing.
          const serverError = (response.data as { error?: { code?: string; message?: string } } | undefined)?.error;
          if (serverError?.message) {
            setError(serverError.message);
            console.error('[SagaPay:Shipping] Ingen fraktalternativer:', serverError.code, '—', serverError.message);
          } else {
            setError(L.noShippingAvailable);
          }
        }
      } catch (err) {
        setError(L.shippingFetchError);
      } finally {
        setIsLoading(false);
      }
    };

    fetchShippingOptions();
  }, [sessionId, shippingAddress?.postalCode, customShippingOptions, useBuiltInShipping, skipShipping]);

  // Don't render anything if shipping should be skipped (AFTER all hooks)
  if (skipShipping) {
    return null;
  }

  /**
   * Shape one shipping option the way the store wants it.
   *
   * Shared by the click handler and by continue, so the summary can be updated
   * the moment a choice is made without also running the navigation and the
   * API call.
   */
  const toStoreShipping = (selected: typeof options[number]) => {
    const deliveryDisplay = getDeliveryDisplay(selected.estimatedDelivery);

    // Parse delivery days from estimatedDelivery data (not display string)
    let deliveryDays = 3; // default
    if (selected.estimatedDelivery) {
      if (typeof selected.estimatedDelivery === 'object' && selected.estimatedDelivery.date) {
        // Calculate days from date
        const deliveryDate = new Date(selected.estimatedDelivery.date);
        const now = new Date();
        deliveryDays = Math.max(1, Math.ceil((deliveryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
      } else if (typeof selected.estimatedDelivery === 'string') {
        // Try to extract number from string like "2-4 virkedager"
        const match = selected.estimatedDelivery.match(/(\d+)/);
        if (match) deliveryDays = parseInt(match[1], 10);
      }
    }

    return {
      id: selected.id,
      provider: selected.provider,
      method: selected.id,
      displayName: selected.name || selected.displayName || L.deliveryLabel,
      description: selected.description || '',
      price: { amount: selected.price, currency: market.defaultCurrency },
      deliveryDays,
      estimatedDelivery: { date: '', display: deliveryDisplay },
      isPickup: selected.isPickup || false,
    };
  };

  /**
   * Picking an option must move the order total straight away.
   *
   * The store was only told about the choice on continue, so the summary sat at
   * the cart total while the header above it read "Inkl. MVA & frakt" — the
   * customer selected Ekspress at 149 kr and the amount did not budge. The
   * price they are agreeing to has to be visible before they commit to it.
   */
  const handleSelect = (optionId: string) => {
    setSelectedOption(optionId);
    const picked = options.find(o => o.id === optionId);
    if (picked) selectShipping(toStoreShipping(picked));
  };

  /**
   * Save the delivery choice, then move on — in that order.
   *
   * This used to navigate first and fire the API call afterwards, unawaited.
   * The payment step creates the payment order the moment it opens, so it read
   * a session that had no shipping on it yet and built the order from the cart
   * alone. The customer saw "Betal 1 548,00" and the card was charged 1 299,00:
   * the shop paid for delivery, every single order.
   *
   * It is a wait of a few hundred milliseconds, with the button showing it. If
   * the call fails we continue anyway rather than trapping the customer — the
   * amount is then still wrong, but the payment step rebuilds the order from
   * the session, and a stuck checkout is worse than a retryable one.
   */
  const handleContinue = async () => {
    const selected = options.find(o => o.id === selectedOption);
    if (!selected || savingShipping) {
      return;
    }

    selectShipping(toStoreShipping(selected));

    if (sessionId) {
      setSavingShipping(true);
      try {
        // Bounded: waiting for correctness is right, waiting forever is not.
        // If the call hangs, the customer moves on rather than staring at a
        // spinner — the payment step rebuilds the order from the session, and
        // changing delivery again updates it.
        await Promise.race([
          selectShippingApi(sessionId, selected.id, selected.price, selected.provider),
          new Promise((resolve) => setTimeout(resolve, 6000)),
        ]);
      } catch {
        // Non-fatal — see above.
      } finally {
        setSavingShipping(false);
      }
    }

    setStep('payment');
  };

  const numberLocale = market.sdkLocale === 'no' ? 'nb-NO' : market.sdkLocale === 'sv' ? 'sv-SE' : market.sdkLocale === 'da' ? 'da-DK' : market.sdkLocale === 'fi' ? 'fi-FI' : 'en-US';

  const formatPrice = (priceInMinor: number) => {
    if (priceInMinor === 0) return L.freeLabel;
    return `${formatAmount(priceInMinor, numberLocale)} ${L.currencySymbol}`;
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>{L.fetchingShippingFor} {shippingAddress?.postalCode}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Determine if this is a configuration error or a transient/API error
    const isConfigError = error === L.noShippingConfigured;
    const errorTitle = isConfigError ? L.shippingNotConfiguredTitle : L.shippingErrorTitle;
    const errorDesc = isConfigError ? L.shippingNotConfiguredDesc : error;
    
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h3>{errorTitle}</h3>
          <p>{errorDesc}</p>
          <p className={styles.errorHint}>{L.shippingContactStore}</p>
          <Button variant="secondary" onClick={() => setStep('address')}>
            {L.goBackLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title} id="shipping-heading">{L.shippingTitle}</h2>
        <p className={styles.subtitle}>{L.shippingSubtitle}</p>
      </div>

      {/* Free shipping progress bar */}
      {freeShippingProgress && !freeShippingProgress.achieved && (
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${Math.min(freeShippingProgress.percentage, 100)}%` }}
            />
          </div>
          <p className={styles.progressMessage}>{freeShippingProgress.message}</p>
        </div>
      )}
      {freeShippingProgress?.achieved && (
        <div className={styles.progressAchieved}>
          <span className={styles.progressCheckmark}>✓</span>
          <p className={styles.progressMessage}>{freeShippingProgress.message}</p>
        </div>
      )}

      <div 
        className={styles.options} 
        role="radiogroup" 
        aria-labelledby="shipping-heading"
      >
        {options.map((option, index) => (
          <div
            key={option.id}
            role="radio"
            aria-checked={selectedOption === option.id}
            /* Roving tabindex: a radiogroup is one stop in the tab order, and
               you move within it using the arrow keys. Giving every option
               tabIndex={0} made Tab walk through all of them instead. */
            tabIndex={selectedOption === option.id || (!selectedOption && index === 0) ? 0 : -1}
            data-shipping-index={index}
            className={`${styles.option} ${selectedOption === option.id ? styles.optionSelected : ''}`}
            onClick={() => handleSelect(option.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(option.id);
                return;
              }

              // Arrow keys move the selection, per the WAI-ARIA radiogroup
              // pattern. Without this the group could only be operated with a
              // pointer, or by tabbing to each option in turn.
              const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1
                : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1
                : 0;
              if (!step) return;

              e.preventDefault();
              const next = (index + step + options.length) % options.length;
              handleSelect(options[next].id);
              const el = e.currentTarget.parentElement?.querySelector<HTMLElement>(
                `[data-shipping-index="${next}"]`
              );
              el?.focus();
            }}
          >
            <div className={`${styles.radio} ${selectedOption === option.id ? styles.radioSelected : ''}`} aria-hidden="true" />
            <div className={styles.optionLogo} aria-hidden="true">
              {getProviderLogo(option)}
            </div>
            <div className={styles.optionContent}>
              {(option.isCheapest || (option.isFastest && !option.isCheapest)) && (
                <div className={styles.badgeRow}>
                  {option.isCheapest && <span className={`${styles.badge} ${styles.badgeCheapest}`}>{L.cheapestBadge}</span>}
                  {option.isFastest && !option.isCheapest && <span className={`${styles.badge} ${styles.badgeFastest}`}>{L.fastestBadge}</span>}
                </div>
              )}
              <p className={styles.optionName}>
                {option.name || option.displayName || L.deliveryOptionFallback}
              </p>
              <p className={styles.optionDelivery}>{option.description || ''} • {getDeliveryDisplay(option.estimatedDelivery, L.unknownDeliveryTime)}</p>
              {/* Show savings info below the delivery text */}
              {option.showSavings && option.discountAmount != null && option.discountAmount > 0 && (
                <p className={styles.savingsText}>
                  Du sparer {formatPrice(option.discountAmount)}
                </p>
              )}
            </div>
            <div className={styles.priceColumn}>
              {/* Show pricing label (e.g. "Gratis frakt") or calculated price */}
              {option.isFreeShipping && option.pricingLabel ? (
                <span className={`${styles.optionPrice} ${styles.freeTag}`} aria-label={L.freeShippingAria}>
                  {option.pricingLabel}
                </span>
              ) : (
                <span className={`${styles.optionPrice} ${option.price === 0 ? styles.freeTag : ''}`} aria-label={option.price === 0 ? L.freeShippingAria : `${option.price / 100} ${L.currencySymbol}`}>
                  {formatPrice(option.price)}
                </span>
              )}
              {/* Show strikethrough original price when discount is applied */}
              {option.showOriginalPrice && option.originalPrice != null && option.discountAmount != null && option.discountAmount > 0 && (
                <span className={styles.originalPrice}>
                  {formatPrice(option.originalPrice)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={() => setStep('address')}
          className={styles.backButton}
          aria-label={L.goBackToStep + ' ' + L.stepAddress}
        >
          {L.backButton}
        </Button>
        <Button
          onClick={handleContinue}
          loading={savingShipping}
          className={styles.continueButton}
          aria-label={L.continueToPayment}
        >
          {L.continueToPayment}

        </Button>
      </div>
    </div>
  );
}
